/* eslint-disable no-nested-ternary */
import { useEffect, useState } from 'react'
import * as XLSX from 'xlsx'
import { Column } from 'primereact/column'
import { ColumnGroup } from 'primereact/columngroup'
import { Row } from 'primereact/row'
import swalAlert from '../../../utils/helpers/swalAlert.js'
import useLoading from '../../../utils/hooks/useLoading.js'
import { formatCurrency } from '../../../utils/helpers/common.js'
import useHandlerBasicActions from '../../../utils/hooks/useHandlerBasicActions.js'
import DataTableComp from '../../../components/common/data-table/DataTableComp.jsx'
import AccountingEntryService from '../../../services/accounting-entry/accountingEntry.service.js'
import DataTableAccountProjects from '../../../components/accounting-entry/DataTableAccountProjects.jsx'
import HeaderViewV2 from '../../../components/common/common-header-v2/HeaderViewV2.jsx'
import AppSection from '../../../component/layout/AppSection'
import AccountingEntryCommonHeader from '../nav/AccountingEntryCommonHeader'
import AppToolbar from '../../../component/layout/AppToolbar'
import AppDateInput from '../../../component/form/AppDateInput'
import EntityDropdown from '../../../component/form/EntityDropdown'
import { useTranslation } from 'react-i18next'
import AppButton from '../../../component/common/AppButton.tsx'

const AccountingEntryGenerator = () => {
  const { t } = useTranslation()

  const storedCompanyCode =
    localStorage.getItem('AccountingEntry.companyCode') || null
  const storedPeriod = localStorage.getItem('AccountingEntry.period')

  const [companyCode, setCompanyCode] = useState(storedCompanyCode || null)
  const [period, setPeriod] = useState(storedPeriod || null)

  const [generating, startGenerating, stopGenerating] = useLoading(false)
  const [year, setYear] = useState(null)
  const [month, setMonth] = useState(null)

  const Views = {
    AccountingEntry: 0,
    Analitics: 1,
  }

  const [accountingEntryData, setAccountingEntryData] = useState({})
  const [currentView, setCurrentView] = useState(Views.AccountingEntry)

  useEffect(() => {
    setCompanyCode(storedCompanyCode || null)
  }, [storedCompanyCode])
  useEffect(() => {
    setPeriod(storedPeriod || null)
  }, [storedPeriod])

  useEffect(() => {
    getLastAccountingData()
  }, [companyCode, year, month])
  useEffect(() => {
    setYear(
      new Date(period).getFullYear() > 1970
        ? new Date(period).getFullYear()
        : null,
    )
    setMonth(
      new Date(period).getFullYear() > 1970
        ? new Date(period).getMonth() + 1
        : null,
    )
  }, [period])
  useEffect(() => {
    dataFacade[Views.Analitics].reload()
    dataFacade[Views.AccountingEntry].reload()
  }, [accountingEntryData])

  const handlePeriodChange = (value) => {
    setPeriod(value)
    localStorage.setItem('AccountingEntry.period', value)
  }

  const handleCompanyCodeChange = (value) => {
    setCompanyCode(value)
    localStorage.setItem('AccountingEntry.companyCode', value)
  }

  const getLastAccountingData = async () => {
    const response = await AccountingEntryService.getAccountingEntryLast({
      year,
      month,
      companyCode: String(companyCode),
    })
    if (response) setAccountingEntryData(response)
  }

  const handleGenerate = async () => {
    if (period && companyCode) {
      swalAlert.loadinng(t('generating'))
      startGenerating()
      const response = await AccountingEntryService.generate({
        year,
        month,
        accountingEntryName: t('accountingEntry'),
        companyCode: String(companyCode),
      })
      setAccountingEntryData(response)
      stopGenerating()
    }
  }

  const handleDownload = async () => {
    // From backend NOT WORKING
    // const response = await AccountingEntryService.download(accountingEntryData?.resp?._id)
    // const filename = accountingEntryData?.resp?._id
    // saveAsExcelFile(response, filename)
    // Frontend mode:
    const workbook = XLSX.utils.table_to_book(
      document.getElementsByClassName('p-datatable-table')[0],
    )
    const filename = `${currentView === Views.AccountingEntry ? 'Asiento' : 'Analitica'}_${companyCode}_${year}_${month}_${accountingEntryData?.resp?._id}.xlsx`
    XLSX.writeFile(workbook, filename)
  }

  const handleSend = async () => {
    if (accountingEntryData?.resp?._id) {
      swalAlert.loadinng(t('sending'))
      startGenerating()
      const response = await AccountingEntryService.send(
        accountingEntryData?.resp?._id,
      )
      setAccountingEntryData(response)
      stopGenerating()
    }
  }

  const changeView = () => {
    setCurrentView(
      currentView === Views.AccountingEntry
        ? Views.Analitics
        : Views.AccountingEntry,
    )
  }

  const dataFacade = [
    useHandlerBasicActions({
      getAll: () => accountingEntryData,
    }),
    useHandlerBasicActions({
      getAll: ({ filter, sort, limit, skip }) =>
        AccountingEntryService.analitics.getAccountingEntryAnalitics({
          id: accountingEntryData?.resp?._id,
          filter,
          sort,
          skip,
        }) || {},
    }),
  ]

  const footerToolbar = (
    <div style={{ marginTop: '1rem' }}>
      <HeaderViewV2
        handlerHeaderActions={{
          primaryButton: handleDownload,
          primaryButtonText: 'download',
          primaryButtonIcon: 'pi-download',
          secondaryButton: handleSend,
          secondaryButtonText: accountingEntryData?.resp?.isInSage
            ? 'button.forward'
            : 'button.send',
          secondaryButtonIcon: 'pi-send',
        }}
      />
    </div>
  )

  const analiticsGroup = (data) => (
    <DataTableAccountProjects header={false} data={data.analitics} />
  )

  const footerGroup = [
    <ColumnGroup>
      <Row key={String(Views.AccountingEntry)}>
        <Column
          key={String(Views.AccountingEntry).concat('-0')}
          footer="Total"
          colSpan={2}
        />
        <Column
          key={String(Views.AccountingEntry).concat('-1')}
          className="text-monospace"
          footer={formatCurrency(accountingEntryData?.resp?.totalCredit)}
          align="right"
        />
        <Column
          key={String(Views.AccountingEntry).concat('-2')}
          className="text-monospace"
          footer={formatCurrency(accountingEntryData?.resp?.totalDebit)}
          align="right"
        />
        <Column
          key={String(Views.AccountingEntry).concat('-3')}
          className={`text-monospace ${accountingEntryData?.resp?.total ? (accountingEntryData?.resp?.total === 0 ? 'bg-success' : 'bg-danger') : ''}`}
          style={{ textAlign: 'right' }}
          footer={formatCurrency(accountingEntryData?.resp?.total)}
        />
      </Row>
    </ColumnGroup>,
    <ColumnGroup>
      <Row key={String(Views.Analitics)}>
        <Column
          key={String(Views.AccountingEntry).concat('-0')}
          footer="Total"
          colSpan={2}
        />
        <Column
          key={String(Views.AccountingEntry).concat('-1')}
          className="text-monospace"
          footer={formatCurrency(accountingEntryData?.resp?.total)}
          align="right"
        />
        <Column key={String(Views.AccountingEntry).concat('-2')} colSpan={3} />
      </Row>
    </ColumnGroup>,
  ]

  const pagerOptions = {
    ...dataFacade[currentView]?.pagerOptions,
    totalRecords: dataFacade[currentView]?.data?.total,
  }

  const dataTable = [
    !generating &&
    companyCode !== null &&
    companyCode !== undefined &&
    accountingEntryData?.resp ? (
      <DataTableComp
        loading={dataFacade?.loading}
        pagerOptions={pagerOptions}
        tableOptions={{
          className: 'table-accounting-entry',
          // header: accountingEntryData?.resp?.data?.length > 0 && dtHeaderToolbar,
          value: accountingEntryData?.resp?.data,
          responsiveLayout: 'scroll',
          scrollable: true,
          // TODO: Comentar con Verónica
          // scrollHeight: DataTableFun.responsiveHeight(),
          footerColumnGroup: footerGroup[Views.AccountingEntry],
        }}
        autoColumnKey
        dataKey="_id"
        columns={[
          <Column
            className="text-monospace"
            field="account"
            header={t('account')}
            style={{ width: '10%' }}
            body={(rowData) => String(rowData.account)}
            sortable
          />,
          <Column
            field="accountName"
            header={t('accountName')}
            style={{ width: '50%' }}
            body={(rowData) =>
              rowData.accountName && String(rowData.accountName)
            }
            sortable
          />,
          <Column
            className="text-monospace"
            field="credit"
            header={t('credit')}
            style={{ width: '20%' }}
            body={(rowData) => formatCurrency(rowData.credit)}
            align="right"
            sortable
          />,
          <Column
            className="text-monospace"
            field="debit"
            header={t('debit')}
            body={(rowData) => formatCurrency(rowData.debit)}
            align="right"
            sortable
          />,
          <Column header={t('total')} align="right" />,
        ]}
      />
    ) : null,
    !generating &&
    companyCode !== null &&
    companyCode !== undefined &&
    dataFacade[Views.Analitics]?.data?.resp?.data ? (
      <DataTableComp
        loading={dataFacade?.loading}
        pagerOptions={pagerOptions}
        footer={footerToolbar}
        tableOptions={{
          // header: accountingEntryData?.resp?.data?.length > 0 && dtHeaderToolbar,
          value: dataFacade[Views.Analitics]?.data?.resp?.data,
          responsiveLayout: 'scroll',
          scrollable: true,
        }}
        autoColumnKey
        dataKey="_id"
        columns={[
          <Column
            field="account"
            className="text-monospace"
            header={t('account')}
            style={{ width: '10%' }}
            sortable
          />,
          <Column
            field="accountName"
            header={t('accountName')}
            style={{ width: '30%' }}
            body={(rowData) =>
              rowData.accountName && String(rowData.accountName)
            }
            sortable
          />,
          <Column
            field="analitics"
            header={t('projects')}
            style={{ width: '60%' }}
            body={analiticsGroup}
          />,
        ]}
      />
    ) : null,
  ]

  return (
    <AppSection
      header={<AccountingEntryCommonHeader />}
      subheader={
        <AppToolbar
          leftSection={[
            <EntityDropdown
              //   miw={280}
              //   labelh="company"
              entity="accountingEntryCompany"
              value={companyCode}
              onChange={({ value }) => handleCompanyCodeChange(value)}
            />,
            <AppDateInput
              disabled={dataFacade[currentView].loading || generating}
              value={period}
              onChange={handlePeriodChange}
            />,
          ]}
          rightSection={[
            <AppButton
              preset="download"
              disabled={
                generating ||
                !accountingEntryData?.resp?._id ||
                dataFacade?.loading ||
                dataFacade?.data?.total <= 0
              }
              onClick={handleDownload}
            />,
            <AppButton
              preset="send"
              disabled={generating || dataFacade[currentView].loading}
              onClick={handleSend}
            />,
            // <Divider orientation="vertical" mx={themeCfg?.default?.padding} />,
            <AppButton
              entity="eye"
              label={
                currentView === 0 ? 'showAnalytics' : 'showAccountingEntry'
              }
              disabled={
                generating ||
                !accountingEntryData?.resp?._id ||
                dataFacade?.loading ||
                dataFacade?.data?.total <= 0
              }
              onClick={changeView}
            />,
            <AppButton
              entity={!generating ? 'play' : 'loader'}
              bg="moure-orange"
              label="generate"
              disabled={generating || dataFacade[currentView].loading}
              onClick={handleGenerate}
            />,
          ]}
        />
      }
    >
      {dataTable[currentView]}
    </AppSection>
  )
}

export default AccountingEntryGenerator
